import CarrierSelector from '@/common/components/CarrierSelector/CarrierSelector'
import DateRangeFilter from '@/common/components/DateRangeFilter/DateRangeFilter'
import { createEmptyFilter } from '@/common/components/FiltersDrawer/helpers'
import RegionFilter from '@/common/components/RegionFilter/RegionFilter'

import { filterNames } from '../constants'
import ClaimStatusSelector from './ClaimStatusSelector/ClaimStatusSelector'
import ClaimTypeSelector from './ClaimTypeSelector/ClaimTypeSelector'
import LocationSelector from './LocationSelector/LocationSelector'
import NormalizedTrackingStatusSelector from './NormalizedTrackingStatusSelector/NormalizedTrackingStatusSelector'
import ParcelExceptionsSelector from './ParcelExceptionsSelector/ParcelExceptionsSelector'
import ParcelStatusSelector from './ParcelStatusSelector/ParcelStatusSelector'
import ParcelTypeSelector from './ParcelTypeSelector/ParcelTypeSelector'

const arrayFilterNames = [
  filterNames.parcelType,
  filterNames.shipmentStatus,
  filterNames.shipmentNormalizedTrackingStatus,
  filterNames.shipmentOrigin,
  filterNames.shipmentDestination,
  filterNames.shipmentsCarrier,
  filterNames.claimStatus,
  filterNames.claimType,
]

export const dateFilterConfig = {
  [filterNames.creationDate]: {
    dateRange: 'creationDates',
    dateStart: filterNames.creationDateStart,
    dateEnd: filterNames.creationDateEnd,
    daysGte: filterNames.shipmentCreationDaysGte,
    daysLte: filterNames.shipmentCreationDaysLte,
  },
  [filterNames.shipmentExternalCreated]: {
    dateRange: 'shipmentExternalCreatedDates',
    dateStart: filterNames.shipmentExternalCreatedGte,
    dateEnd: filterNames.shipmentExternalCreatedLte,
    daysGte: filterNames.shipmentExternalCreatedDaysGte,
    daysLte: filterNames.shipmentExternalCreatedDaysLte,
  },
  [filterNames.shipmentPackedAt]: {
    dateRange: 'shipmentPackedAtDates',
    dateStart: filterNames.shipmentPackedAtGte,
    dateEnd: filterNames.shipmentPackedAtLte,
    daysGte: filterNames.shipmentPackedAtDaysGte,
    daysLte: filterNames.shipmentPackedAtDaysLte,
  },
  [filterNames.shipmentReadyDate]: {
    dateRange: 'shipmentReadyDate',
    dateStart: filterNames.shipmentReadyDateGte,
    dateEnd: filterNames.shipmentReadyDateLte,
    daysGte: filterNames.shipmentReadyDateDaysGte,
    daysLte: filterNames.shipmentReadyDateDaysLte,
  },
  [filterNames.shipmentShippedAt]: {
    dateRange: 'shipmentShippedAtDates',
    dateStart: filterNames.shipmentShippedAtGte,
    dateEnd: filterNames.shipmentShippedAtLte,
    daysGte: filterNames.shipmentShippedAtDaysGte,
    daysLte: filterNames.shipmentShippedAtDaysLte,
  },
  [filterNames.shipmentDeliveredAt]: {
    dateRange: 'shipmentDeliveredAtDates',
    dateStart: filterNames.shipmentDeliveredAtGte,
    dateEnd: filterNames.shipmentDeliveredAtLte,
    daysGte: filterNames.shipmentDeliveredAtDaysGte,
    daysLte: filterNames.shipmentDeliveredAtDaysLte,
  },
  [filterNames.claimCreatedAt]: {
    dateRange: 'claimCreatedAtDates',
    dateStart: filterNames.claimCreatedAtGte,
    dateEnd: filterNames.claimCreatedAtLte,
    daysGte: filterNames.claimCreatedAtDaysGte,
    daysLte: filterNames.claimCreatedAtDaysLte,
  },
}

export const dateFilterNames = Object.keys(dateFilterConfig)

const hasValue = value => value != null && value !== ''

const isValueActive = (type, valueMap) => {
  if (dateFilterNames.includes(type)) {
    const config = dateFilterConfig[type]
    const range = valueMap[config.dateRange]
    return Boolean(
      hasValue(range?.startDate) ||
      hasValue(range?.endDate) ||
      hasValue(valueMap[config.daysGte]) ||
      hasValue(valueMap[config.daysLte])
    )
  }

  const value = valueMap[type]
  if (Array.isArray(value)) return value.length > 0
  return hasValue(value)
}

const buildValueFromFilters = (type, filters) => {
  if (dateFilterNames.includes(type)) {
    const config = dateFilterConfig[type]
    return {
      [config.dateRange]: filters[config.dateRange] || null,
      [config.daysGte]: filters[config.daysGte] ?? null,
      [config.daysLte]: filters[config.daysLte] ?? null,
    }
  }

  return { [type]: filters[type] }
}

export const getDefaultFilterValue = type => {
  if (dateFilterNames.includes(type)) {
    const config = dateFilterConfig[type]
    return { [config.dateRange]: null, [config.daysGte]: null, [config.daysLte]: null }
  }

  if (arrayFilterNames.includes(type)) {
    return { [type]: [] }
  }

  return { [type]: '' }
}

export const getInitialFilters = (filters, filterTypes = []) => {
  const initialFilters = filterTypes
    .filter(ft => isValueActive(ft.value, filters))
    .map(ft => ({
      id: crypto.randomUUID(),
      type: ft.value,
      value: buildValueFromFilters(ft.value, filters),
    }))

  return initialFilters.length > 0 ? initialFilters : [createEmptyFilter()]
}

export const isFilterValid = filter => {
  if (!filter.type) return true

  return isValueActive(filter.type, filter.value)
}

export const getFilterLabel = (filter, filterTypes = []) => {
  return filterTypes?.find(ft => ft.value === filter.type)?.label
}

export const applyFilters = (filters, onChangeFilter) => {
  for (const filter of filters) {
    const { type, value } = filter

    if (dateFilterNames.includes(type)) {
      const config = dateFilterConfig[type]
      onChangeFilter(config.dateStart, value[config.dateRange]?.startDate ?? null, false)
      onChangeFilter(config.dateEnd, value[config.dateRange]?.endDate ?? null, false)
      onChangeFilter(config.daysGte, value[config.daysGte] ?? null, false)
      onChangeFilter(config.daysLte, value[config.daysLte] ?? null, false)
      continue
    }

    onChangeFilter(type, value[type], false)
  }
}

export const clearAllFilters = onChangeFilter => {
  onChangeFilter(filterNames.parcelType, [], false)
  onChangeFilter(filterNames.shipmentStatus, [], false)
  onChangeFilter(filterNames.shipmentNormalizedTrackingStatus, [], false)
  onChangeFilter(filterNames.shipmentOrigin, [], false)
  onChangeFilter(filterNames.shipmentDestination, [], false)
  onChangeFilter(filterNames.creationDateStart, null, false)
  onChangeFilter(filterNames.creationDateEnd, null, false)
  onChangeFilter(filterNames.shipmentCreationDaysGte, null, false)
  onChangeFilter(filterNames.shipmentCreationDaysLte, null, false)
  onChangeFilter(filterNames.shipmentException, null, false)
  onChangeFilter(filterNames.shipmentsCarrier, [], false)
  onChangeFilter(filterNames.shipmentsCarrierService, null, false)
  onChangeFilter(filterNames.shipmentExternalCreatedGte, null, false)
  onChangeFilter(filterNames.shipmentExternalCreatedLte, null, false)
  onChangeFilter(filterNames.shipmentExternalCreatedDaysGte, null, false)
  onChangeFilter(filterNames.shipmentExternalCreatedDaysLte, null, false)
  onChangeFilter(filterNames.shipmentPackedAtGte, null, false)
  onChangeFilter(filterNames.shipmentPackedAtLte, null, false)
  onChangeFilter(filterNames.shipmentPackedAtDaysGte, null, false)
  onChangeFilter(filterNames.shipmentPackedAtDaysLte, null, false)
  onChangeFilter(filterNames.shipmentReadyDateGte, null, false)
  onChangeFilter(filterNames.shipmentReadyDateLte, null, false)
  onChangeFilter(filterNames.shipmentReadyDateDaysGte, null, false)
  onChangeFilter(filterNames.shipmentReadyDateDaysLte, null, false)
  onChangeFilter(filterNames.shipmentShippedAtGte, null, false)
  onChangeFilter(filterNames.shipmentShippedAtLte, null, false)
  onChangeFilter(filterNames.shipmentShippedAtDaysGte, null, false)
  onChangeFilter(filterNames.shipmentShippedAtDaysLte, null, false)
  onChangeFilter(filterNames.shipmentDeliveredAtGte, null, false)
  onChangeFilter(filterNames.shipmentDeliveredAtLte, null, false)
  onChangeFilter(filterNames.shipmentDeliveredAtDaysGte, null, false)
  onChangeFilter(filterNames.shipmentDeliveredAtDaysLte, null, false)
  onChangeFilter(filterNames.shipmentLocation, null, false)
  onChangeFilter(filterNames.claimStatus, [], false)
  onChangeFilter(filterNames.claimType, [], false)
  onChangeFilter(filterNames.claimCreatedAtGte, null, false)
  onChangeFilter(filterNames.claimCreatedAtLte, null, false)
  onChangeFilter(filterNames.claimCreatedAtDaysGte, null, false)
  onChangeFilter(filterNames.claimCreatedAtDaysLte, null, false)
}

export const filterFields = {
  [filterNames.parcelType]: ({ value, onChangeValue }) => (
    <ParcelTypeSelector value={value.parcelType} onChange={v => onChangeValue({ parcelType: v })} />
  ),
  [filterNames.shipmentStatus]: ({ value, onChangeValue, fieldProps }) => (
    <ParcelStatusSelector
      value={value.shipmentStatus}
      onChange={v => onChangeValue({ shipmentStatus: v })}
      direction={fieldProps.direction}
    />
  ),
  [filterNames.shipmentNormalizedTrackingStatus]: ({ value, onChangeValue }) => (
    <NormalizedTrackingStatusSelector
      value={value.shipmentNormalizedTrackingStatus}
      onChange={v => onChangeValue({ shipmentNormalizedTrackingStatus: v })}
    />
  ),
  [filterNames.shipmentOrigin]: ({ value, onChangeValue, fieldProps }) => (
    <RegionFilter
      label={fieldProps.t('Origen')}
      value={value.shipmentOrigin}
      onChange={v => onChangeValue({ shipmentOrigin: v })}
      regions={fieldProps.regions}
    />
  ),
  [filterNames.shipmentDestination]: ({ value, onChangeValue, fieldProps }) => (
    <RegionFilter
      label={fieldProps.t('Destino')}
      value={value.shipmentDestination}
      onChange={v => onChangeValue({ shipmentDestination: v })}
      regions={fieldProps.regions}
    />
  ),
  [filterNames.shipmentLocation]: ({ value, onChangeValue, fieldProps }) => (
    <LocationSelector
      value={value.shipmentLocation}
      onChange={v => onChangeValue({ shipmentLocation: v })}
      locations={fieldProps.locations}
    />
  ),
  [filterNames.shipmentException]: ({ value, onChangeValue }) => (
    <ParcelExceptionsSelector value={value.shipmentException} onChange={v => onChangeValue({ shipmentException: v })} />
  ),
  [filterNames.shipmentsCarrier]: ({ value, onChangeValue, fieldProps }) => (
    <CarrierSelector
      value={value.shipmentsCarrier || []}
      onChange={v => onChangeValue({ shipmentsCarrier: v })}
      carriers={fieldProps.carriers || []}
      multiple
      label={fieldProps.t('Transportista')}
      showIcon={false}
    />
  ),
  [filterNames.shipmentsCarrierService]: ({ value, onChangeValue, fieldProps }) => (
    <CarrierSelector
      value={value.shipmentsCarrierService}
      onChange={v => onChangeValue({ shipmentsCarrierService: v?.id })}
      carriers={fieldProps.carrierServices || []}
      label={fieldProps.t('Servicio de logística')}
      showIcon={false}
    />
  ),
  [filterNames.creationDate]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.creationDates,
        dynamicValue: { daysGte: value.shipmentCreationDaysGte, daysLte: value.shipmentCreationDaysLte },
      }}
      onChangeRange={v => onChangeValue({ creationDates: v })}
      onChangeDaysGte={v => onChangeValue({ shipmentCreationDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ shipmentCreationDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.shipmentExternalCreated]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.shipmentExternalCreatedDates,
        dynamicValue: {
          daysGte: value.shipmentExternalCreatedDaysGte,
          daysLte: value.shipmentExternalCreatedDaysLte,
        },
      }}
      onChangeRange={v => onChangeValue({ shipmentExternalCreatedDates: v })}
      onChangeDaysGte={v => onChangeValue({ shipmentExternalCreatedDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ shipmentExternalCreatedDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.shipmentPackedAt]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.shipmentPackedAtDates,
        dynamicValue: { daysGte: value.shipmentPackedAtDaysGte, daysLte: value.shipmentPackedAtDaysLte },
      }}
      onChangeRange={v => onChangeValue({ shipmentPackedAtDates: v })}
      onChangeDaysGte={v => onChangeValue({ shipmentPackedAtDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ shipmentPackedAtDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.shipmentReadyDate]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.shipmentReadyDate,
        dynamicValue: { daysGte: value.shipmentReadyDateDaysGte, daysLte: value.shipmentReadyDateDaysLte },
      }}
      onChangeRange={v => onChangeValue({ shipmentReadyDate: v })}
      onChangeDaysGte={v => onChangeValue({ shipmentReadyDateDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ shipmentReadyDateDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.shipmentShippedAt]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.shipmentShippedAtDates,
        dynamicValue: { daysGte: value.shipmentShippedAtDaysGte, daysLte: value.shipmentShippedAtDaysLte },
      }}
      onChangeRange={v => onChangeValue({ shipmentShippedAtDates: v })}
      onChangeDaysGte={v => onChangeValue({ shipmentShippedAtDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ shipmentShippedAtDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.shipmentDeliveredAt]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.shipmentDeliveredAtDates,
        dynamicValue: { daysGte: value.shipmentDeliveredAtDaysGte, daysLte: value.shipmentDeliveredAtDaysLte },
      }}
      onChangeRange={v => onChangeValue({ shipmentDeliveredAtDates: v })}
      onChangeDaysGte={v => onChangeValue({ shipmentDeliveredAtDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ shipmentDeliveredAtDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.claimStatus]: ({ value, onChangeValue }) => (
    <ClaimStatusSelector value={value.claimStatus} onChange={v => onChangeValue({ claimStatus: v })} />
  ),
  [filterNames.claimType]: ({ value, onChangeValue }) => (
    <ClaimTypeSelector value={value.claimType} onChange={v => onChangeValue({ claimType: v })} />
  ),
  [filterNames.claimCreatedAt]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.claimCreatedAtDates,
        dynamicValue: { daysGte: value.claimCreatedAtDaysGte, daysLte: value.claimCreatedAtDaysLte },
      }}
      onChangeRange={v => onChangeValue({ claimCreatedAtDates: v })}
      onChangeDaysGte={v => onChangeValue({ claimCreatedAtDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ claimCreatedAtDaysLte: v })}
      showTypeSelector
    />
  ),
}
